import React, { createContext, useContext, useEffect, useState, ReactNode } from "react"
import { getUserPermissions } from "../utils/api"
import { getUserEmail } from "../utils/auth"
import { UserPermissions } from "../utils/permissions"

interface SessionContextType {
  userId: string | null
  permissions: UserPermissions | null
  isLoading: boolean
  error: string | null
  refreshPermissions: () => Promise<void>
}

// isLoading: true by default so a consumer rendered outside <SessionProvider>
// never mistakes the absence of a session for "authorized: false" -
// checkAuthorization() treats isLoading as "don't know yet", not "denied".
const defaultSessionValue: SessionContextType = {
  userId: null,
  permissions: null,
  isLoading: true,
  error: null,
  refreshPermissions: async () => {},
}

const SessionContext = createContext<SessionContextType>(defaultSessionValue)

export function useSession() {
  return useContext(SessionContext)
}

interface SessionProviderProps {
  children: ReactNode
}

/**
 * Fetches the current user's RBAC permissions once and caches them in memory for the
 * lifetime of the browser session (i.e. as long as this provider stays mounted at the
 * app root), so every IfAuthorized gate reads the same cached list instead of each one
 * making its own network call.
 * Call refreshPermissions() to re-fetch without a page reload (e.g. after a role change).
 */
export function SessionProvider({ children }: SessionProviderProps) {
  const [userId, setUserId] = useState<string | null>(null)
  const [permissions, setPermissions] = useState<UserPermissions | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchSession = async (silent = false) => {
    try {
      if (!silent) {
        setIsLoading(true)
      }
      setError(null)

      const email = getUserEmail()
      setUserId(email)

      const userPermissions = await getUserPermissions()
      setPermissions(userPermissions)
    } catch (err) {
      console.error("SessionProvider: failed to fetch user permissions", err)
      setError(err instanceof Error ? err.message : "Unknown error")
      setPermissions(null)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchSession()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refreshPermissions = () => fetchSession(true)

  const value: SessionContextType = { userId, permissions, isLoading, error, refreshPermissions }

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export default SessionContext
